/**
 * RKG Governance & Explainability Dashboard
 * Fase 9-11 — Observabilidade, ativação e validação do motor preditivo RKG
 */
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  ShieldCheck,
  AlertTriangle,
  BarChart3,
  Cpu,
  GitBranch,
  Eye,
  Bell,
  Users,
  Zap,
  FileCheck,
  Loader2,
} from 'lucide-react';

// ─── Hooks ──────────────────────────────────────────────────────────────────

function useModelVersions() {
  return useQuery({
    queryKey: ['rkg-model-versions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_model_versions' as any)
        .select('*')
        .order('release_date', { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });
}

function useExecutionHealth() {
  return useQuery({
    queryKey: ['rkg-execution-health'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_vw_model_execution_health' as any)
        .select('*');
      if (error) throw error;
      return data as any[];
    },
  });
}

function useExecutionLog() {
  return useQuery({
    queryKey: ['rkg-execution-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_model_execution_log' as any)
        .select('*')
        .order('execution_timestamp', { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as any[];
    },
  });
}

function useTopRiskCauses() {
  return useQuery({
    queryKey: ['rkg-top-risk-causes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_vw_top_risk_causes' as any)
        .select('*')
        .limit(20);
      if (error) throw error;
      return data as any[];
    },
  });
}

function useActivityRiskContribution() {
  return useQuery({
    queryKey: ['rkg-activity-risk-contribution'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_vw_activity_risk_contribution' as any)
        .select('*')
        .limit(30);
      if (error) throw error;
      return data as any[];
    },
  });
}

function useOperationalValue() {
  return useQuery({
    queryKey: ['rkg-operational-value'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_vw_prediction_operational_value' as any)
        .select('*');
      if (error) throw error;
      return data as any[];
    },
  });
}

function useAlertFrequency() {
  return useQuery({
    queryKey: ['rkg-alert-frequency'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_vw_risk_alert_frequency' as any)
        .select('*');
      if (error) throw error;
      return data as any[];
    },
  });
}

function useActivationStatus() {
  return useQuery({
    queryKey: ['rkg-activation-status'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_vw_activation_status' as any)
        .select('*')
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });
}

function useValidationReports() {
  return useQuery({
    queryKey: ['rkg-validation-reports'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_validation_reports' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data as any[];
    },
  });
}

function useActivationLog() {
  return useQuery({
    queryKey: ['rkg-activation-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rkg_activation_log' as any)
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data as any[];
    },
  });
}

function useRunValidation() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const resp = await supabase.functions.invoke('rkg-activate', {
        body: { action: 'validate' },
      });
      if (resp.error) throw new Error(resp.data?.error || resp.error.message);
      if (!resp.data?.success) throw new Error(resp.data?.error || 'Validação falhou');
      return resp.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['rkg-validation-reports'] });
      queryClient.invalidateQueries({ queryKey: ['rkg-activation-status'] });
      queryClient.invalidateQueries({ queryKey: ['rkg-activation-log'] });
      toast({
        title: 'Validação RKG concluída',
        description: `Status: ${data.overall_status} · ${data.shadow_summary?.total} contratos analisados`,
      });
    },
    onError: (err: Error) => {
      toast({ title: 'Erro na validação', description: err.message, variant: 'destructive' });
    },
  });
}

// ─── Components ─────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, variant = 'default' }: {
  icon: React.ElementType; label: string; value: string | number; sub?: string;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}) {
  const colors = {
    default: 'text-primary',
    success: 'text-emerald-500',
    warning: 'text-amber-500',
    danger: 'text-destructive',
  };
  return (
    <Card>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${colors[variant]}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <Icon className={`w-5 h-5 ${colors[variant]} opacity-60`} />
        </div>
      </CardContent>
    </Card>
  );
}

function ModelVersionsPanel({ versions }: { versions: any[] }) {
  if (!versions.length) return <p className="text-sm text-muted-foreground p-4">Nenhuma versão registrada.</p>;
  return (
    <div className="space-y-3">
      {versions.map((v) => (
        <Card key={v.id}>
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-primary" />
                <span className="font-mono font-semibold text-sm">v{v.model_version}</span>
              </div>
              <Badge variant="outline" className="text-xs">{v.algorithm_type}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{v.notes || '—'}</p>
            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
              <span>Distribuição: <strong>{v.distribution_method}</strong></span>
              <span>Publicado: {new Date(v.release_date).toLocaleDateString('pt-BR')}</span>
            </div>
            {v.parameter_config && (
              <details className="mt-2">
                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Parâmetros</summary>
                <pre className="mt-1 text-xs bg-muted/50 p-2 rounded overflow-x-auto">
                  {JSON.stringify(v.parameter_config, null, 2)}
                </pre>
              </details>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function ExecutionLogTable({ logs }: { logs: any[] }) {
  if (!logs.length) return <p className="text-sm text-muted-foreground p-4">Nenhuma execução registrada.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="text-left py-2 px-3 font-medium">Timestamp</th>
            <th className="text-left py-2 px-3 font-medium">Versão</th>
            <th className="text-right py-2 px-3 font-medium">Iterações</th>
            <th className="text-right py-2 px-3 font-medium">Duração (ms)</th>
            <th className="text-center py-2 px-3 font-medium">Status</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 px-3 font-mono">{new Date(l.execution_timestamp).toLocaleString('pt-BR')}</td>
              <td className="py-2 px-3 font-mono">v{l.model_version}</td>
              <td className="py-2 px-3 text-right">{l.n_simulations?.toLocaleString() ?? '—'}</td>
              <td className="py-2 px-3 text-right">{l.duration_ms?.toLocaleString() ?? '—'}</td>
              <td className="py-2 px-3 text-center">
                {l.status === 'completed' ? (
                  <Badge className="bg-primary/10 text-primary border-0 text-xs">OK</Badge>
                ) : (
                  <Badge variant="destructive" className="text-xs">{l.status}</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TopCausesPanel({ causes }: { causes: any[] }) {
  if (!causes.length) return <p className="text-sm text-muted-foreground p-4">Sem dados de causas.</p>;
  return (
    <div className="space-y-2">
      {causes.map((c, i) => (
        <div key={c.cause_id || i} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/30">
          <span className="text-xs font-mono text-muted-foreground w-6">#{i + 1}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">Causa: {c.cause_id?.slice(0, 8) ?? '—'}...</p>
            <p className="text-xs text-muted-foreground">
              {c.contracts_affected} contratos · {c.simulation_count} simulações
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-semibold">{(Number(c.avg_impact_weight) * 100).toFixed(1)}%</p>
            <p className="text-xs text-muted-foreground">peso médio</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ActivityContributionPanel({ activities }: { activities: any[] }) {
  if (!activities.length) return <p className="text-sm text-muted-foreground p-4">Sem dados de atividades.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="text-left py-2 px-3 font-medium">Atividade</th>
            <th className="text-right py-2 px-3 font-medium">Peso Impacto</th>
            <th className="text-right py-2 px-3 font-medium">Fator Caminho Crítico</th>
            <th className="text-right py-2 px-3 font-medium">Desvios</th>
            <th className="text-right py-2 px-3 font-medium">Causas</th>
          </tr>
        </thead>
        <tbody>
          {activities.map((a, i) => (
            <tr key={a.activity_id || i} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 px-3 font-mono">{a.activity_id?.slice(0, 8) ?? '—'}...</td>
              <td className="py-2 px-3 text-right font-semibold">{(Number(a.avg_impact_weight) * 100).toFixed(1)}%</td>
              <td className="py-2 px-3 text-right">{Number(a.avg_critical_path_factor).toFixed(3)}</td>
              <td className="py-2 px-3 text-right">{a.unique_deviations}</td>
              <td className="py-2 px-3 text-right">{a.unique_causes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OperationalValuePanel({ data }: { data: any[] }) {
  if (!data.length) return <p className="text-sm text-muted-foreground p-4">Nenhum contrato piloto com dados de uso.</p>;
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <Card key={d.contract_id || i}>
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs">{d.contract_id?.slice(0, 8)}...</span>
              <Badge variant={d.operational_value_tier === 'high' ? 'default' : 'outline'} className="text-xs">
                {d.operational_value_tier === 'high' ? 'Alto Valor' : d.operational_value_tier === 'medium' ? 'Valor Médio' : 'Baixo Valor'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div><p className="text-muted-foreground">Visualizações</p><p className="font-semibold text-sm">{d.total_views}</p></div>
              <div><p className="text-muted-foreground">Usadas em Decisão</p><p className="font-semibold text-sm">{d.used_in_decisions}</p></div>
              <div><p className="text-muted-foreground">Taxa Suporte</p><p className="font-semibold text-sm">{d.decision_support_rate}%</p></div>
              <div><p className="text-muted-foreground">Alertas Resolvidos</p><p className="font-semibold text-sm">{d.resolved_alerts}/{d.total_alerts}</p></div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AlertFrequencyPanel({ alerts }: { alerts: any[] }) {
  if (!alerts.length) return <p className="text-sm text-muted-foreground p-4">Nenhum alerta RKG registrado.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="text-left py-2 px-3 font-medium">Contrato</th>
            <th className="text-center py-2 px-3 font-medium">Nível</th>
            <th className="text-right py-2 px-3 font-medium">Total</th>
            <th className="text-right py-2 px-3 font-medium">Abertos</th>
            <th className="text-right py-2 px-3 font-medium">Resolvidos</th>
            <th className="text-right py-2 px-3 font-medium">Atraso Médio (dias)</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((a, i) => (
            <tr key={i} className="border-b border-border/50 hover:bg-muted/30">
              <td className="py-2 px-3 font-mono">{a.contract_id?.slice(0, 8)}...</td>
              <td className="py-2 px-3 text-center">
                <Badge variant={a.risk_level === 'high' ? 'destructive' : 'outline'} className="text-xs">{a.risk_level}</Badge>
              </td>
              <td className="py-2 px-3 text-right">{a.alert_count}</td>
              <td className="py-2 px-3 text-right">{a.open_alerts}</td>
              <td className="py-2 px-3 text-right">{a.resolved_alerts}</td>
              <td className="py-2 px-3 text-right">{Number(a.avg_predicted_delay).toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActivationStatusPanel({ status, onRunValidation, isValidating }: {
  status: any; onRunValidation: () => void; isValidating: boolean;
}) {
  if (!status) return <p className="text-sm text-muted-foreground p-4">Carregando status...</p>;

  return (
    <div className="space-y-4">
      {/* Flags */}
      <Card>
        <CardHeader className="py-3 px-5">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4" /> Feature Flags
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'RKG Ativo', value: status.rkg_enabled },
              { label: 'MC Paralelo', value: status.mc_parallel },
              { label: 'Aprendizado', value: status.incremental_learning },
              { label: 'Piloto', value: status.pilot_enabled },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-2 text-xs">
                {f.value ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
                <span className={f.value ? 'font-medium' : 'text-muted-foreground'}>{f.label}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Cpu} label="Runs Completos" value={status.total_completed_runs ?? 0} variant="success" />
        <StatCard icon={TrendingUp} label="Parâmetros Aprendidos" value={status.learned_parameters_count ?? 0} variant="default" />
        <StatCard icon={Users} label="Contratos Piloto" value={status.active_pilot_contracts ?? 0} variant="default" />
        <StatCard icon={FileCheck} label="Última Validação" value={status.last_validation_report ? new Date(status.last_validation_report).toLocaleDateString('pt-BR') : '—'} variant="default" />
      </div>

      {/* Action */}
      <Button onClick={onRunValidation} disabled={isValidating} className="gap-2">
        {isValidating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
        {isValidating ? 'Executando validação...' : 'Executar Validação e Relatório'}
      </Button>
    </div>
  );
}

function ValidationReportsPanel({ reports }: { reports: any[] }) {
  if (!reports.length) return <p className="text-sm text-muted-foreground p-4">Nenhum relatório de validação.</p>;
  return (
    <div className="space-y-3">
      {reports.map((r) => (
        <Card key={r.id}>
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileCheck className="w-4 h-4 text-primary" />
                <span className="text-xs font-mono">{new Date(r.created_at).toLocaleString('pt-BR')}</span>
              </div>
              <Badge
                variant={r.overall_status === 'healthy' ? 'default' : r.overall_status === 'warning' ? 'outline' : 'destructive'}
                className="text-xs"
              >
                {r.overall_status === 'healthy' ? '✓ Saudável' : r.overall_status === 'warning' ? '⚠ Atenção' : '✗ Crítico'}
              </Badge>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3">
              <div><p className="text-muted-foreground">Contratos</p><p className="font-semibold">{r.total_contracts}</p></div>
              <div><p className="text-muted-foreground">Divergência Média</p><p className="font-semibold">{r.avg_p50_delta != null ? `${Math.round(r.avg_p50_delta)}%` : '—'}</p></div>
              <div>
                <p className="text-muted-foreground">Shadow</p>
                <p className="font-semibold text-xs">
                  {r.shadow_comparison?.aligned ?? 0} ok · {r.shadow_comparison?.moderate ?? 0} mod · {r.shadow_comparison?.high ?? 0} high
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Aprendizado</p>
                <p className="font-semibold">
                  {r.learning_status?.enabled ? `${r.learning_status.parameters_updated ?? 0} atualizados` : 'Desativado'}
                </p>
              </div>
            </div>

            {r.recommendations?.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Recomendações</p>
                {r.recommendations.map((rec: string, i: number) => (
                  <p key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                    <span className="text-primary">•</span> {rec}
                  </p>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const RkgGovernancePage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { data: versions = [], isLoading: loadingVersions } = useModelVersions();
  const { data: health = [], isLoading: loadingHealth } = useExecutionHealth();
  const { data: logs = [], isLoading: loadingLogs } = useExecutionLog();
  const { data: causes = [] } = useTopRiskCauses();
  const { data: activities = [] } = useActivityRiskContribution();
  const { data: operationalValue = [] } = useOperationalValue();
  const { data: alertFrequency = [] } = useAlertFrequency();
  const { data: activationStatus } = useActivationStatus();
  const { data: validationReports = [] } = useValidationReports();
  const runValidation = useRunValidation();

  const currentHealth = health[0];

  return (
    <div className="space-y-6">
      {/* Header — hidden when embedded as tab in Observabilidade */}
      {!embedded && (
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          Governança RKG
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Observabilidade, auditoria, ativação e validação do motor preditivo baseado no Risk Knowledge Graph.
        </p>
      </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={GitBranch}
          label="Versão Atual"
          value={versions[0]?.model_version ? `v${versions[0].model_version}` : '—'}
          sub={versions[0]?.release_date ? new Date(versions[0].release_date).toLocaleDateString('pt-BR') : undefined}
        />
        <StatCard
          icon={Cpu}
          label="Execuções"
          value={currentHealth?.total_executions ?? 0}
          sub={currentHealth ? `${currentHealth.successful} ok · ${currentHealth.failed} falhas` : undefined}
          variant={currentHealth?.failed > 0 ? 'warning' : 'success'}
        />
        <StatCard
          icon={Clock}
          label="Tempo Médio"
          value={currentHealth?.avg_duration_ms ? `${Math.round(currentHealth.avg_duration_ms)}ms` : '—'}
          sub={currentHealth?.max_duration_ms ? `Máx: ${Math.round(currentHealth.max_duration_ms)}ms` : undefined}
        />
        <StatCard
          icon={Activity}
          label="Taxa de Sucesso"
          value={currentHealth?.success_rate_pct ? `${currentHealth.success_rate_pct}%` : '—'}
          variant={Number(currentHealth?.success_rate_pct) >= 95 ? 'success' : Number(currentHealth?.success_rate_pct) >= 80 ? 'warning' : 'danger'}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ativacao" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="ativacao">Ativação</TabsTrigger>
          <TabsTrigger value="validacao">Validação</TabsTrigger>
          <TabsTrigger value="execucoes">Execuções</TabsTrigger>
          <TabsTrigger value="versoes">Versões</TabsTrigger>
          <TabsTrigger value="causas">Top Causas</TabsTrigger>
          <TabsTrigger value="atividades">Atividades</TabsTrigger>
          <TabsTrigger value="valor">Valor Operacional</TabsTrigger>
          <TabsTrigger value="alertas">Alertas RKG</TabsTrigger>
        </TabsList>

        <TabsContent value="ativacao">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Status de Ativação do Motor RKG
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ActivationStatusPanel
                status={activationStatus}
                onRunValidation={() => runValidation.mutate()}
                isValidating={runValidation.isPending}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="validacao">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileCheck className="w-4 h-4" />
                Relatórios de Validação
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ValidationReportsPanel reports={validationReports} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="execucoes">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Log de Execuções (últimas 50)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingLogs ? <p className="text-sm text-muted-foreground p-4">Carregando...</p> : <ExecutionLogTable logs={logs} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="versoes">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <GitBranch className="w-4 h-4" />
                Histórico de Versões do Modelo
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingVersions ? <p className="text-sm text-muted-foreground">Carregando...</p> : <ModelVersionsPanel versions={versions} />}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="causas">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Principais Causas de Risco (Agregado)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TopCausesPanel causes={causes} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="atividades">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Contribuição de Risco por Atividade
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ActivityContributionPanel activities={activities} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="valor">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Valor Operacional por Contrato Piloto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <OperationalValuePanel data={operationalValue} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alertas">
          <Card>
            <CardHeader className="py-3 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Frequência de Alertas RKG
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AlertFrequencyPanel alerts={alertFrequency} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RkgGovernancePage;
